import {
    AppBar,
    Badge,
    Button,
    Grid,
    IconButton,
    ListItem,
    ListItemText,
    MenuItem,
    Popover,
    Snackbar,
    Toolbar,
    Tooltip,
    Typography,
    useMediaQuery,
} from "@material-ui/core";
import { createStyles, fade, makeStyles, Theme, useTheme } from "@material-ui/core/styles";
import {
    AccountCircle,
    Archive,
    Assessment,
    Ballot,
    Home,
    Notifications,
    PowerSettingsNew,
    Settings,
    SettingsSystemDaydream,
    Translate,
    Warning,
} from "@material-ui/icons";
import MuiAlert, { AlertProps } from "@material-ui/lab/Alert";
import * as signalR from "@microsoft/signalr";
import { HubConnectionState } from "@microsoft/signalr";
import * as Icons from "@mui/icons-material/";
import { Menu } from "@mui/material";
import { useSnackbar } from "notistack";
import { Profile } from "oidc-client";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useHistory, useLocation } from "react-router-dom";
import {
    IndexModel,
    MarkAsReadCommand,
    ProjectDto,
    RenderModel2,
    UserAlarmsDto,
    UserNotificationDto,
} from "src/App/api/APIClient";
import { authService } from "src/App/auth/AuthorizeService";
import { ChartsModel } from "src/App/Models/SignalModels";
import { BlindFlangePanels } from "src/App/NewModels/BlindFlanges/Models";
import BDTLogoWhite from "../../../../assets/images/BIM_logo_white.png";
import IlbocLogo from "../../../../assets/images/ilbocLogoWhite.png";
import i18n from "../../../../i18n";
import { fetchBlindFlangesPanels } from "../../../api/Fetchers";
import { alarmsService, notificationsService } from "../../../api/Services";
import { ApplicationPaths } from "../../../auth/ApiAuthorizationConstants";
import CookiesModel from "../../../CookiesModel";
import { BDTRoutes } from "../../../helpers/applicationConstants";
import { usePureEffect } from "../../../helpers/usePureEffect";
import { FullAssetInformation } from "../../../Models/AssetInformationModels";
import { PanelAppContext } from "../../../PanelAppProvider";
import PanelProjectContext from "../../PanelProjectProvider";
import { ViewerMode } from "../../Project/Viewer/ForgeViewer/BIMDigitalTwinViewer/Utils";
import useAsset from "../../Project/Viewer/hooks/useAsset";
import AlertPopover from "./Popovers/AlertPopover/AlertPopover";
import HistoricalAdvertises from "./Popovers/HistoricalAdvertises/HistoricalAdvertises";

function Alert(props: AlertProps) {
    return <MuiAlert elevation={24} variant="filled" {...props} />;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            display: "flex",
        },
        appBar: {
            zIndex: theme.zIndex.drawer + 2,
            background: "linear-gradient(35deg, rgba(150,0,255,1) 20%, rgba(239,51,64,1) 60%)",
            boxShadow: `0  -2px 6px 0px ${theme.palette.secondary.main}`,
        },
        toolBar: {
            justifyContent: "space-between",
        },

        menuButton: {
            color: theme.palette.background.default,
        },
        search: {
            position: "relative",
            borderRadius: theme.shape.borderRadius,
            backgroundColor: fade(theme.palette.secondary.light, 0.35),
            "&:hover": {
                backgroundColor: fade(theme.palette.secondary.light, 0.55),
            },
            marginLeft: 0,
            width: "100%",
            [theme.breakpoints.up("sm")]: {
                marginLeft: theme.spacing(1),
                width: "auto",
            },
            [theme.breakpoints.down("xs")]: {
                display: "none",
            },
        },
        searchIcon: {
            padding: theme.spacing(0, 2),
            height: "100%",
            position: "absolute",
            pointerEvents: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
        },
        inputRoot: {
            color: "inherit",
        },
        userMenuItemIcon: {
            paddingLeft: 10,
        },
        typography: {
            padding: theme.spacing(2),
        },
        logo: {
            position: "absolute",
            marginLeft: "48%",
        },
        dev: {
            position: "absolute",
            width: "100%",
            height: "80%",
            backgroundImage: "url(https://cdn-icons-png.flaticon.com/512/126/126428.png)",
            backgroundSize: "contain",
            backgroundRepeat: "space",
            opacity: 0.035,
            zIndex: -1,
        },
        customBadge: {
            backgroundColor: "#851932",
        },
        preprod: {
            position: "absolute",
            // background: "#ffffff30",
            // borderRadius: "20px",
            display: "flex",
            alignContent: "center",
            alignItems: "center",
            padding: "0px 10px",
        },
    })
);

const notificationsConnection = new signalR.HubConnectionBuilder()
    .withUrl("/hubs/notifications", {
        accessTokenFactory: async () => {
            const token = await authService.getAccessToken();
            return token !== null ? token : "";
        },
        withCredentials: true,
    })
    .configureLogging(signalR.LogLevel.Error)
    .withAutomaticReconnect()
    .build();

const alarmsConnection = new signalR.HubConnectionBuilder()
    .withUrl("/hubs/alarms", {
        accessTokenFactory: async () => {
            const token = await authService.getAccessToken();
            return token !== null ? token : "";
        },
        withCredentials: true,
    })
    .configureLogging(signalR.LogLevel.Error)
    .withAutomaticReconnect()
    .build();

interface IObjTest {
    xcoord: number;
    ycoord: number;
    data: ChartsModel;
}

interface IPanelProjectContext {
    setProject: React.Dispatch<React.SetStateAction<ProjectDto>>;
    project: ProjectDto;
    setNotifications: React.Dispatch<React.SetStateAction<UserNotificationDto[]>>;
    notifications: UserNotificationDto[];
    setNotificationsCount: React.Dispatch<React.SetStateAction<number>>;
    notificationsCount: number;
    setAlarms: React.Dispatch<React.SetStateAction<UserAlarmsDto[]>>;
    alarms: UserAlarmsDto[];
    setAlarmsCount: React.Dispatch<React.SetStateAction<number>>;
    alarmsCount: number;
    isViewerReady: boolean;
    setView: React.Dispatch<React.SetStateAction<string>>;
    setAssetSelection: React.Dispatch<React.SetStateAction<IndexModel | undefined>>;
    setIsRightPanelOpen: React.Dispatch<React.SetStateAction<boolean>>;
    setIsRenderingItems: React.Dispatch<React.SetStateAction<boolean>>;
    setUserProfile: React.Dispatch<React.SetStateAction<Profile>>;
    userId: string | undefined;
    setUserId: React.Dispatch<React.SetStateAction<string | undefined>>;
    userProfilePhoto: string | undefined;
    setIsViewerLoading: React.Dispatch<React.SetStateAction<boolean>>;
    setBlindFlangeRightPanelData: React.Dispatch<React.SetStateAction<BlindFlangePanels | null>>;
    setIsBFRightPanelButtonsLoading: React.Dispatch<React.SetStateAction<boolean>>;
    setIsBlindFlangeRightPanelOpen: React.Dispatch<React.SetStateAction<boolean>>;
    setViewerMode: React.Dispatch<React.SetStateAction<ViewerMode>>;
    setHoveredDeviceInfo: React.Dispatch<React.SetStateAction<IObjTest | undefined>>;
    setRenderModel: React.Dispatch<React.SetStateAction<RenderModel2[] | undefined>>;
    viewerMode: ViewerMode;
}

interface IPanelAppProvider {
    cookies: CookiesModel | undefined;
    setCookies: React.Dispatch<React.SetStateAction<CookiesModel | undefined>>;
}

const TopBar = (): JSX.Element => {
    const history = useHistory();
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation("main");

    const emptyAlertStyles = { width: "25%", maxWidth: "300px" };
    const alertStyles = { width: "30%", minWidth: "450px" };

    const {
        project,
        notifications,
        setNotifications,
        notificationsCount,
        setNotificationsCount,
        alarms,
        setAlarms,
        alarmsCount,
        setAlarmsCount,
        isViewerReady,
        setView,
        setAssetSelection,
        setIsRightPanelOpen,
        setIsRenderingItems,
        setUserProfile,
        userId,
        setUserId,
        userProfilePhoto,
        setIsViewerLoading,
        setBlindFlangeRightPanelData,
        setIsBFRightPanelButtonsLoading,
        setIsBlindFlangeRightPanelOpen,
        setViewerMode,
        setHoveredDeviceInfo,
        setRenderModel,
        viewerMode,
    } = React.useContext<IPanelProjectContext>(PanelProjectContext);

    const { cookies, setCookies } = React.useContext<IPanelAppProvider>(PanelAppContext);

    const location = useLocation();
    const classes = useStyles();

    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("xs"));
    const [isDev, setIsDev] = useState<boolean>(true);

    const [popUpNotifications, setPopUpNotifications] = useState(false);
    const togglePopupNotifications = () => {
        setPopUpNotifications(!popUpNotifications);
    };
    const [popUpAlarms, setPopUpAlarms] = useState(false);
    const togglePopupAlarms = () => {
        setPopUpAlarms(!popUpAlarms);
    };

    const [snackbarAlarms, setSnackbarAlarms] = React.useState<{
        state: boolean;
        signalId: string;
        description: string;
        date: string;
        type: string;
    }>({
        state: false,
        signalId: "",
        description: "",
        date: "",
        type: "",
    });

    const handleCloseSnackbarAlarms = (event?: React.SyntheticEvent, reason?: string) => {
        if (reason === "clickaway") {
            return;
        }

        setSnackbarAlarms({
            state: false,
            signalId: "",
            description: "",
            date: "",
            type: "",
        });
    };

    const [notificationsHistoryList, setNotificationsHistoryList] = useState<UserNotificationDto[]>(
        []
    );
    const getNotificationsHistoryList = async () => {
        notificationsService
            .getAllNotificationsByUser(project.id)
            .then((res) => {
                setNotificationsHistoryList(res.data);
            })
            .catch((err) => {
                enqueueSnackbar(`${err.errorDescription.message}`, {
                    variant: "error",
                    anchorOrigin: {
                        horizontal: "right",
                        vertical: "bottom",
                    },
                });
                console.error(err);
            });
    };
    const [alarmsHistoryList, setAlarmsHistoryList] = useState<UserAlarmsDto[]>([]);
    const getAlarmsHistoryList = async () => {
        alarmsService
            .getAllAlarmsByUser({
                projectId: project.id,
            })
            .then((res) => {
                setAlarmsHistoryList(res.data);
            })
            .catch((err) => {
                enqueueSnackbar(`${err.errorDescription.message}`, {
                    variant: "error",
                    anchorOrigin: {
                        horizontal: "right",
                        vertical: "bottom",
                    },
                });
                console.error(err);
            });
    };

    const heightCalc = window.innerHeight - 400;

    const [anchorElPopOver, setAnchorElPopOver] = useState<HTMLButtonElement | null>(null);
    const [anchorElPopOverAlarms, setAnchorElPopOverAlarms] = useState<HTMLButtonElement | null>(
        null
    );
    const [profile, setProfile] = useState<Profile | null>(null);
    const [isAdmin, setIsAdmin] = useState<boolean>(false);

    useEffect(() => {
        authService.getUser().then((user) => {
            if (user && user.profile) {
                setUserProfile(user?.profile);
                setProfile(user?.profile);
                setUserId(user.profile.oid);
            }
            setUserId(user?.profile.oid as string);
            const validEncoded = user?.access_token.split(".")[1];
            const decondedAccessToken = validEncoded ? atob(validEncoded) : "yay";
            const isAdminX = decondedAccessToken.includes(`"roles":["administrator"]`);
            setIsAdmin(isAdminX);
        });
    }, []);

    const handleClickPopOver = (event: React.MouseEvent<HTMLButtonElement>) => {
        notificationsService.getUnreadNotifications(project.id).then((res) => {
            if (res && res.data) {
                setNotifications(res.data);
                // setNotificationsCount(res.data.map((x) => x.ids.split(",")).length);
                setNotificationsCount(res.data.length);
            } else {
                setNotifications([]);
                setNotificationsCount(0);
            }
        });
        setAnchorElPopOver(event.currentTarget);
    };
    const handleClickPopOverAlarms = (event: React.MouseEvent<HTMLButtonElement>) => {
        alarmsService.getUnreadAlarms({ projectId: project.id }).then((res) => {
            if (res && res.data) {
                setAlarms(res.data);
                // setAlarmsCount(res.data.length);
            } else {
                setAlarms([]);
                // setAlarmsCount(0);
            }
        });
        setAnchorElPopOverAlarms(event.currentTarget);
    };

    const handleClosePopOver = () => {
        setAnchorElPopOver(null);
    };
    const handleCLosePopOverAlarms = () => {
        setAnchorElPopOverAlarms(null);
    };

    const openPopOver = Boolean(anchorElPopOver);
    const openPopOverAlarms = Boolean(anchorElPopOverAlarms);
    const idPopOver = openPopOver ? "simple-popover" : undefined;
    const idPopOverAlarms = openPopOverAlarms ? "simple-popover" : undefined;

    const [assetIdCommentsSelected, setAssetIdCommentsSelected] = useState<string | undefined>(
        undefined
    );
    const [assetIdSignalsSelected, setAssetIdSignalsSelected] = useState<string | undefined>(
        undefined
    );
    const [assetAlarmTypeSelected, setAssetAlarmTypeSelected] = useState<string | undefined>(
        undefined
    );

    const [dataRigthPanelSelected, setDataRigthPanelSelected] = useState<
        FullAssetInformation | undefined
    >(undefined);

    const { fetchingAssetInformationById } = useAsset();

    const handleCommentsPanelItemClick = (route: string, asset: string) => {
        if (!isViewerReady) history.push(route);

        handleClosePopOver();
        setAssetIdCommentsSelected(asset);
    };

    const handleSignalsPanelItemClick = (route: string, asset: string, type?: string) => {
        if (!isViewerReady) history.push(route);

        handleCLosePopOverAlarms();
        setAssetIdSignalsSelected(asset);

        if (type) setAssetAlarmTypeSelected(type);
    };

    React.useEffect(() => {
        if (isViewerReady && assetIdCommentsSelected !== undefined) {
            setAssetIdCommentsSelected(undefined);
            fetchingAssetInformationById(project.id, assetIdCommentsSelected, true, undefined);
            setView("chat");
        }
        if (
            isViewerReady &&
            assetIdSignalsSelected !== undefined &&
            assetAlarmTypeSelected !== undefined
        ) {
            setAssetIdSignalsSelected(undefined);
            setAssetAlarmTypeSelected(undefined);

            switch (assetAlarmTypeSelected) {
                case "signal":
                    fetchingAssetInformationById(
                        project.id,
                        assetIdSignalsSelected,
                        true,
                        undefined
                    );
                    setView("alarms");
                    break;
                default:
                    window.NOP_VIEWER.clearThemingColors(window.NOP_VIEWER.model);
                    window.NOP_VIEWER.setLightPreset(17);
                    window.NOP_VIEWER.setOpacityModel(1, undefined);
                    window.NOP_VIEWER.setEnvMapBackground(true);
                    window.NOP_VIEWER.deleteElementsSpecularColor();
                    setHoveredDeviceInfo(undefined);
                    setIsRightPanelOpen(false);
                    setIsViewerLoading(true);
                    setRenderModel(undefined);
                    setAssetSelection({});
                    setIsRenderingItems(false);

                    setIsBFRightPanelButtonsLoading(true);
                    fetchBlindFlangesPanels(project.id, {
                        name: assetIdSignalsSelected,
                    })
                        .then((res) => {
                            window.NOP_VIEWER.mode = ViewerMode.BlindFlanges;
                            setViewerMode(window.NOP_VIEWER.mode);
                            setIsBlindFlangeRightPanelOpen(true);
                            setBlindFlangeRightPanelData(res.data);
                            setView("blindFlangeHistory");
                            return res.data;
                        })
                        .catch((err) => {
                            throw err;
                        })
                        .finally(() => {
                            setIsBFRightPanelButtonsLoading(false);
                            setIsViewerLoading(false);
                        });

                    setTimeout(() => {
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        const currentCamera = window.NOP_VIEWER.getCamera().clone() as any;
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        const savedCameraConfig = {
                            position: currentCamera.position.clone(),
                            target: currentCamera.target.clone(),
                            up: currentCamera.up.clone(),
                            aspect: currentCamera.aspect,
                            fov: currentCamera.fov,
                            near: currentCamera.near,
                            far: currentCamera.far,
                            orthoScale: currentCamera.orthoScale,
                            isPerspective: currentCamera.isPerspective,
                        };
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        const newPosition = new THREE.Vector3(
                            savedCameraConfig.position.x - 150,
                            savedCameraConfig.position.y + 250,
                            savedCameraConfig.position.z + 250
                        );
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        const newTarget = new THREE.Vector3(
                            savedCameraConfig.target.x,
                            savedCameraConfig.target.y,
                            savedCameraConfig.target.z
                        );
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        const newUp = new THREE.Vector3(
                            savedCameraConfig.up.x,
                            savedCameraConfig.up.y,
                            savedCameraConfig.up.z
                        );
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        const nav = window.NOP_VIEWER.navigation;
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        nav.setView(newPosition, newTarget);
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        window.NOP_VIEWER.getCamera().up.copy(newUp);
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        window.NOP_VIEWER.getCamera().aspect = savedCameraConfig.aspect;
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        window.NOP_VIEWER.getCamera().fov = savedCameraConfig.fov;
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        window.NOP_VIEWER.getCamera().near = savedCameraConfig.near;
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        window.NOP_VIEWER.getCamera().far = savedCameraConfig.far;
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        window.NOP_VIEWER.getCamera().updateProjectionMatrix();
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        window.NOP_VIEWER.applyCamera(window.NOP_VIEWER.getCamera());
                    }, 3000);

                    break;
            }
        }
    }, [isViewerReady, assetIdCommentsSelected, assetIdSignalsSelected, dataRigthPanelSelected]);

    const subscribeToNotificationsSignalR = async (updatedProjectId: ProjectDto) => {
        authService.getProfile().then((profResponse) => {
            if (profResponse !== null) {
                if (notificationsConnection.state === HubConnectionState.Disconnected)
                    setProfile(profResponse);
                notificationsConnection
                    .start()
                    .then(() => {
                        authService.getProfile().then((prof) => {
                            if (prof !== null) {
                                notificationsConnection.invoke("AddUserConnection", prof.oid);
                                notificationsConnection.on(
                                    "PushNotifications",
                                    (theNewProjectId) => {
                                        notificationsService
                                            .getUnreadNotificationsCount(theNewProjectId)
                                            .then((res) => {
                                                setNotificationsCount(res.data.number);
                                            })
                                            .catch((err) => {
                                                enqueueSnackbar(`${err.errorDescription.message}`, {
                                                    variant: "error",
                                                    anchorOrigin: {
                                                        horizontal: "right",
                                                        vertical: "bottom",
                                                    },
                                                });
                                                console.error(err);
                                            });
                                    }
                                );
                            }
                        });
                    })
                    .catch((err) => {
                        enqueueSnackbar(`${err}`, {
                            variant: "error",
                            anchorOrigin: {
                                horizontal: "right",
                                vertical: "bottom",
                            },
                        });
                        console.error(err);
                    })
                    .finally(() => {
                        authService.getProfile().then((prof) => {
                            if (prof !== null) {
                                notificationsService
                                    .getUnreadNotificationsCount(updatedProjectId.id)
                                    .then((res) => {
                                        setNotificationsCount(res.data.number);
                                    })
                                    .catch((err) => {
                                        enqueueSnackbar(`${err}`, {
                                            variant: "error",
                                            anchorOrigin: {
                                                horizontal: "right",
                                                vertical: "bottom",
                                            },
                                        });
                                        console.error(err);
                                    });
                            }
                        });
                    });
            }
        });
    };

    const subscribeToAlarmsSignalR = async (updatedProjectId: ProjectDto) => {
        authService.getProfile().then((profResponse) => {
            if (profResponse !== null) {
                if (alarmsConnection.state === HubConnectionState.Disconnected)
                    setProfile(profResponse);
                alarmsConnection
                    .start()
                    .then(() => {
                        authService.getProfile().then((prof) => {
                            if (prof !== null) {
                                alarmsConnection.invoke("AddAlarmsConnection", prof.oid);
                                alarmsConnection.on("PushAlarms", (theNewProjectId: string) => {
                                    alarmsService
                                        .getUnreadAlarmsCount({ projectId: theNewProjectId })
                                        .then((res) => {
                                            setAlarmsCount(res.data.number);
                                        })
                                        .catch((err) => {
                                            enqueueSnackbar(`${err.errorDescription.message}`, {
                                                variant: "error",
                                                anchorOrigin: {
                                                    horizontal: "right",
                                                    vertical: "bottom",
                                                },
                                            });
                                            console.error(err);
                                        });

                                    alarmsService
                                        .getUnreadAlarms({ projectId: theNewProjectId })
                                        .then((res) => {
                                            setSnackbarAlarms({
                                                state: true,
                                                signalId: `${res.data[0].aditionalParams}`,
                                                description: `${res.data[0].description}`,
                                                date: `${res.data[0].descriptionParams}`,
                                                type: `${res.data[0].alarmType}`,
                                            });
                                        })
                                        .catch((err) => {});
                                });
                            }
                        });
                    })
                    .catch((err) => {
                        enqueueSnackbar(`${err}`, {
                            variant: "error",
                            anchorOrigin: {
                                horizontal: "right",
                                vertical: "bottom",
                            },
                        });
                        console.error(err);
                    })
                    .finally(() => {
                        authService.getProfile().then((prof) => {
                            if (prof !== null && updatedProjectId.id !== undefined) {
                                alarmsService
                                    .getUnreadAlarmsCount({ projectId: updatedProjectId.id })
                                    .then((res) => {
                                        setAlarmsCount(res.data.number);
                                    })
                                    .catch((err) => {
                                        enqueueSnackbar(`${err}`, {
                                            variant: "error",
                                            anchorOrigin: {
                                                horizontal: "right",
                                                vertical: "bottom",
                                            },
                                        });
                                        console.error("IEPA: ", err);
                                    });
                            }
                        });
                    });
            }
        });
    };

    useEffect(() => {
        const dev =
            window.location.host.includes("localhost") || window.location.host.includes("5050");
        setIsDev(dev);
    }, []);

    usePureEffect(() => {
        setView("");
        setAssetSelection({});
        setIsRightPanelOpen(false);
        setIsRenderingItems(false);

        if (notificationsConnection.state === HubConnectionState.Connected) {
            notificationsConnection.stop().then(() => {
                subscribeToNotificationsSignalR(project);
            });
        } else {
            subscribeToNotificationsSignalR(project);
        }

        if (alarmsConnection.state === HubConnectionState.Connected) {
            alarmsConnection.stop().then(() => {
                subscribeToAlarmsSignalR(project);
            });
        } else {
            subscribeToAlarmsSignalR(project);
        }
    }, [project]);

    const [anchorProfile, setAnchorProfile] = React.useState<null | HTMLElement>(null);
    const handleClickProfile = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorProfile(event.currentTarget);
    };
    const handleCloseProfile = () => {
        setAnchorProfile(null);
    };

    const [anchorSettings, setAnchorSettings] = React.useState<null | HTMLElement>(null);
    const handleClickSettings = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorSettings(event.currentTarget);
    };
    const handleCloseSettings = () => {
        setAnchorSettings(null);
    };

    const handleMenuItemClick = (route: string) => {
        history.push(route);
        handleCloseSettings();
        handleCloseProfile();
    };

    const [anchorLanguages, setAnchorLanguages] = React.useState<null | HTMLElement>(null);
    const handleClickLanguages = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorLanguages(event.currentTarget);
    };
    const handleCloseLanguages = () => {
        setAnchorLanguages(null);
    };

    const handleMenuLanguageClick = (langSelected: string) => {
        i18n.changeLanguage(langSelected);
        // TODO set user language on server
        handleCloseLanguages();
    };

    const handleLogOut = () => {
        authService.getProfile().then((prof) => {
            if (prof !== null) {
                notificationsConnection.invoke("RemoveUserConnection", prof.sub).finally(() => {
                    history.push(ApplicationPaths.LogOut, { local: true });
                    handleCloseSettings();
                    handleCloseProfile();
                });
            }
        });
    };

    const handleReadNotification = async (notId, projectid) => {
        if (notId) {
            const newNotId = notId.split(",").map((x) => Number(x));
            const command: MarkAsReadCommand = { ids: newNotId, projectId: projectid };
            await notificationsService.markAsRead(command);
            notificationsService.getUnreadNotifications(projectid).then((res) => {
                if (res) {
                    setNotifications(res.data);
                    setNotificationsCount(res.data.length);
                } else {
                    setNotifications([]);
                    setNotificationsCount(0);
                }
            });
        }
    };

    const handleReadAlarms = async (notId, projectid) => {
        if (notId) {
            const newNotId = notId.split(",").map((x) => Number(x));
            const command: MarkAsReadCommand = { ids: newNotId, projectId: projectid };
            await alarmsService.markAsRead(command);
            alarmsService.getUnreadAlarms({ projectId: project.id }).then((res) => {
                if (res) {
                    setAlarms(res.data);
                    setAlarmsCount(res.data.length);
                } else {
                    setAlarms([]);
                    setAlarmsCount(0);
                }
            });
        }
    };

    // const [openHandleBlobStorage, setOpenHandleBlobStorage] = useState(false);
    // const handleBlobStorageSelection = () => {
    //     setOpenHandleBlobStorage(!openHandleBlobStorage);
    // };

    // const changeBlobStorage = (event: React.ChangeEvent<HTMLInputElement>) => {
    //     if (blobStorageSelected === "Azure") setBlobStorageSelected("Therefore");
    //     else setBlobStorageSelected("Azure");
    // };

    return (
        <div className={classes.root}>
            <AppBar className={classes.appBar} position="fixed" color="secondary">
                <Toolbar className={classes.toolBar}>
                    <div className={classes.search}>
                        <div className={classes.searchIcon}>
                            {/* Need this empty space. Fdo.Anita */}
                        </div>
                        {/* Need this empty space. Fdo.Anita */}
                    </div>

                    <div className={classes.preprod}>
                        <img src={IlbocLogo} alt="ilboc-logo" width="100px" />
                    </div>
                    <div className={classes.logo}>
                        <Link to={BDTRoutes.Home}>
                            <img height="40" src={BDTLogoWhite} alt="BDT logo" />
                        </Link>
                    </div>

                    {isDev && <div className={classes.dev} />}

                    <div>
                        <Tooltip title={`${t("topbar.home")}`}>
                            <IconButton
                                component={Link}
                                to={BDTRoutes.Home}
                                className={classes.menuButton}
                            >
                                <Home />
                            </IconButton>
                        </Tooltip>

                        <Tooltip title={`${t("topbar.translations")}`}>
                            <IconButton
                                onClick={handleClickLanguages}
                                className={classes.menuButton}
                            >
                                <Translate />
                            </IconButton>
                        </Tooltip>
                        <Menu
                            anchorEl={anchorLanguages}
                            open={Boolean(anchorLanguages)}
                            onClose={handleCloseLanguages}
                            autoFocus={false}
                            anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "right",
                            }}
                            transformOrigin={{
                                vertical: "top",
                                horizontal: "right",
                            }}
                        >
                            <MenuItem
                                style={{ backgroundColor: i18n.language === "es" ? "#F5F5F5" : "" }}
                                onClick={() => handleMenuLanguageClick("es")}
                            >
                                <Typography variant="body1">{t("languages.spanish")}</Typography>
                            </MenuItem>
                            <MenuItem
                                style={{ backgroundColor: i18n.language === "en" ? "#F5F5F5" : "" }}
                                onClick={() => handleMenuLanguageClick("en")}
                            >
                                <Typography variant="body1">{t("languages.english")}</Typography>
                            </MenuItem>
                            <MenuItem
                                style={{ backgroundColor: i18n.language === "fr" ? "#F5F5F5" : "" }}
                                onClick={() => handleMenuLanguageClick("fr")}
                            >
                                <Typography variant="body1">{t("languages.french")}</Typography>
                            </MenuItem>
                        </Menu>

                        {isAdmin && (
                            <Tooltip title={`${t("topbar.settings")}`}>
                                <IconButton
                                    onClick={handleClickSettings}
                                    className={classes.menuButton}
                                >
                                    <Settings />
                                </IconButton>
                            </Tooltip>
                        )}
                        <Menu
                            anchorEl={anchorSettings}
                            anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "right",
                            }}
                            transformOrigin={{
                                vertical: "top",
                                horizontal: "right",
                            }}
                            open={Boolean(anchorSettings)}
                            onClose={handleCloseSettings}
                            autoFocus={false}
                        >
                            <ListItem dense divider>
                                <ListItemText secondary={t("topbar.forge")} />
                            </ListItem>
                            <MenuItem
                                onClick={() => {
                                    handleMenuItemClick(BDTRoutes.Administration.ForgeAccounts);
                                }}
                            >
                                <SettingsSystemDaydream />
                                <span className={classes.userMenuItemIcon}>
                                    {t("topbar.forgeAccountsAdmin")}
                                </span>
                            </MenuItem>
                            <MenuItem
                                onClick={() => {
                                    handleMenuItemClick(BDTRoutes.Administration.Models);
                                }}
                            >
                                <Archive />
                                <span className={classes.userMenuItemIcon}>
                                    {t("topbar.modelsAdmin")}
                                </span>
                            </MenuItem>
                            <ListItem dense divider>
                                <ListItemText secondary={t("topbar.powerBI")} />
                            </ListItem>
                            <MenuItem
                                onClick={() => {
                                    handleMenuItemClick(BDTRoutes.Administration.PowerBIAccounts);
                                }}
                            >
                                <Assessment />
                                <span className={classes.userMenuItemIcon}>
                                    {t("topbar.powerBIAccountsAdmin")}
                                </span>
                            </MenuItem>
                            <ListItem dense divider>
                                <ListItemText secondary={t("topbar.projects")} />
                            </ListItem>
                            <MenuItem
                                onClick={() => {
                                    handleMenuItemClick(BDTRoutes.Administration.Projects);
                                }}
                            >
                                <Ballot />
                                <span className={classes.userMenuItemIcon}>
                                    {t("topbar.projectsAdmin")}
                                </span>
                            </MenuItem>
                        </Menu>

                        <Tooltip title={`${t("topbar.notifications")}`}>
                            <IconButton
                                onClick={(event) => {
                                    handleClickPopOver(event);
                                }}
                                className={classes.menuButton}
                            >
                                <Badge
                                    badgeContent={notificationsCount}
                                    classes={{ badge: classes.customBadge }}
                                >
                                    <Notifications />
                                </Badge>
                            </IconButton>
                        </Tooltip>
                        <Popover
                            id={idPopOver}
                            open={openPopOver}
                            anchorEl={anchorElPopOver}
                            onClose={handleClosePopOver}
                            anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "right",
                            }}
                            transformOrigin={{
                                vertical: "top",
                                horizontal: "right",
                            }}
                            PaperProps={{
                                style: notificationsCount === 0 ? emptyAlertStyles : alertStyles,
                            }}
                        >
                            {notificationsCount > 0 ? (
                                <AlertPopover
                                    alerts={notifications}
                                    handleReadNotifications={handleReadNotification}
                                    handlePanelItemClick={handleCommentsPanelItemClick}
                                    notificationType="notification"
                                />
                            ) : (
                                <Typography className={classes.typography}>
                                    {t("topbar.noNotifications")}
                                </Typography>
                            )}

                            <Grid container justify="center">
                                <Grid item>
                                    <Button
                                        style={{ marginBottom: "15px", marginTop: "20px" }}
                                        variant="contained"
                                        color="primary"
                                        onClick={() => {
                                            togglePopupNotifications();
                                            getNotificationsHistoryList();
                                            handleClosePopOver();
                                        }}
                                    >
                                        {t("topbar.showNotificationsHistory")}
                                    </Button>
                                </Grid>
                            </Grid>
                        </Popover>
                        {popUpNotifications && (
                            <HistoricalAdvertises
                                open={popUpNotifications}
                                close={togglePopupNotifications}
                                information={notificationsHistoryList}
                                type="notifications"
                                setAdvertise={setNotifications}
                                setAdvertiseCount={setNotificationsCount}
                            />
                        )}

                        <Tooltip title={`${t("topbar.alarms")}`}>
                            <IconButton
                                onClick={(event) => {
                                    handleClickPopOverAlarms(event);
                                }}
                                className={classes.menuButton}
                            >
                                <Badge
                                    badgeContent={alarmsCount}
                                    classes={{ badge: classes.customBadge }}
                                >
                                    <Warning />
                                </Badge>
                            </IconButton>
                        </Tooltip>
                        <Popover
                            id={idPopOverAlarms}
                            open={openPopOverAlarms}
                            anchorEl={anchorElPopOverAlarms}
                            onClose={handleCLosePopOverAlarms}
                            anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "right",
                            }}
                            transformOrigin={{
                                vertical: "top",
                                horizontal: "right",
                            }}
                            PaperProps={{
                                style: alarmsCount === 0 ? emptyAlertStyles : alertStyles,
                            }}
                        >
                            {alarmsCount > 0 ? (
                                <AlertPopover
                                    alerts={alarms}
                                    handlePanelItemClick={handleSignalsPanelItemClick}
                                    handleReadNotifications={handleReadAlarms}
                                    notificationType="alarm"
                                />
                            ) : (
                                <Typography className={classes.typography}>
                                    {t("topbar.noAlarms")}
                                </Typography>
                            )}

                            <Grid container justify="center">
                                <Grid item>
                                    <Button
                                        style={{ marginBottom: "15px", marginTop: "20px" }}
                                        variant="contained"
                                        color="primary"
                                        onClick={() => {
                                            togglePopupAlarms();
                                            getAlarmsHistoryList();
                                            handleCLosePopOverAlarms();
                                        }}
                                    >
                                        {t("topbar.showAlarmsHistory")}
                                    </Button>
                                </Grid>
                            </Grid>
                        </Popover>
                        {popUpAlarms && (
                            <HistoricalAdvertises
                                open={popUpAlarms}
                                close={togglePopupAlarms}
                                information={alarmsHistoryList}
                                type="alarms"
                                setAdvertise={setAlarms}
                                setAdvertiseCount={setAlarmsCount}
                            />
                        )}

                        <Tooltip title={`${t("topbar.profile")}`}>
                            <IconButton onClick={handleClickProfile} className={classes.menuButton}>
                                {userProfilePhoto ? (
                                    <img
                                        alt="fotodeperfil"
                                        src={userProfilePhoto}
                                        style={{
                                            width: "30px",
                                            height: "30px",
                                            borderRadius: "50%",
                                        }}
                                    />
                                ) : (
                                    <AccountCircle />
                                )}
                            </IconButton>
                        </Tooltip>
                        <Menu
                            anchorEl={anchorProfile}
                            anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "right",
                            }}
                            transformOrigin={{
                                vertical: "top",
                                horizontal: "right",
                            }}
                            open={Boolean(anchorProfile)}
                            onClose={handleCloseProfile}
                        >
                            {/* TODO User profile */}
                            {/* <MenuItem onClick={() => handleMenuItemClick(BDTRoutes)}>
                                <Person />{" "}
                                <span className={classes.userMenuItemIcon}>{t("profile")}</span>
                            </MenuItem> */}
                            <MenuItem>
                                <span className={classes.userMenuItemIcon}>
                                    {profile?.name ? profile.name : t("login.unknownUser")}
                                </span>
                            </MenuItem>
                            <MenuItem onClick={handleLogOut}>
                                <PowerSettingsNew />
                                <span className={classes.userMenuItemIcon}>
                                    {t("topbar.logout")}
                                </span>
                            </MenuItem>
                        </Menu>
                    </div>
                </Toolbar>
            </AppBar>

            <Snackbar
                open={snackbarAlarms.state}
                autoHideDuration={9000}
                onClose={handleCloseSnackbarAlarms}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                }}
            >
                <Alert
                    onClose={handleCloseSnackbarAlarms}
                    severity="warning"
                    style={{ backgroundColor: "#FFF633", color: "black", fontWeight: "bold" }}
                    action={
                        <IconButton
                            size="small"
                            aria-label="close"
                            color="inherit"
                            onClick={(ev) => {
                                handleSignalsPanelItemClick(
                                    BDTRoutes.Project.Viewer.Viewer.Builder(project.id),
                                    snackbarAlarms.signalId,
                                    snackbarAlarms.type
                                );
                                handleCloseSnackbarAlarms(ev);
                            }}
                        >
                            {React.createElement(Icons.LastPage)}
                        </IconButton>
                    }
                >
                    <Typography variant="body2">{snackbarAlarms.description}</Typography>
                    <Typography variant="body2">{snackbarAlarms.date}</Typography>
                </Alert>
            </Snackbar>
        </div>
    );
};

const TopBarM = React.memo(TopBar);

export default TopBarM;
